"use client";

import React, { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={navigationMenuTriggerStyle()}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 bg-background/98 backdrop-blur-md pt-24 px-6 flex flex-col md:hidden animate-in fade-in duration-200">
      <nav className="flex flex-col gap-6 text-center">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onClose} className="text-2xl font-display font-semibold text-foreground hover:text-primary transition-colors" data-index={i}>
            {item.label}
          </Link>)}
        <div className="mt-8 flex flex-col gap-4" data-index="actions">
          <Button asChild size="lg" className="w-full btn-primary rounded-full text-lg">
            <Link to="tel:9197030041">
              <Phone className="mr-2 size-5" />
              (919) 703-0041
            </Link>
          </Button>
        </div>
      </nav>
    </div>;
}
function HeaderContent() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  return <>
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80 transition-all duration-300">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <Link to="/" className="flex items-center gap-2 z-50 relative hover:opacity-90 transition-opacity">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1418843/img/smart_buy_appliances_logo.png" alt="Smart Buy Appliances" className="h-8 md:h-10 w-auto object-contain" />
          </Link>

          {}
          <DesktopNav />

          {}
          <div className="flex items-center gap-4 z-50 relative">
            <Button asChild variant="default" className="hidden md:flex bg-accent hover:bg-accent-hover text-white shadow-soft hover-lift">
              <Link to="tel:9197030041">
                <Phone className="mr-2 size-4" />
                Call Now
              </Link>
            </Button>

            <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={toggleMenu} aria-label="Toggle Menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </header>

      {}
      <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>;
}
export default function Hero() {
  const {
    ref: badgeRef,
    isVisible: isBadgeVisible
  } = useRevealOnScroll({
    once: true
  });
  const {
    ref: titleRef,
    isVisible: isTitleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: btnRef,
    isVisible: isBtnVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section id="hero" className="relative min-h-screen flex flex-col bg-background">
      <MenuProvider menu_id="18" className="contents">
        <HeaderContent />
      </MenuProvider>

      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1418843/img/modern_kitchen_appliances_hero.png)]" />
          <div className="absolute inset-0 bg-black/65 backdrop-blur-[2px]" />
        </div>

        {}
        <div className="container relative z-10 mx-auto px-4 py-20 md:py-32 flex flex-col items-center text-center">
          <div ref={badgeRef} className={`transition-all duration-700 transform ${isBadgeVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm md:text-base font-bold uppercase tracking-widest bg-secondary text-white border-none shadow-soft">
              Steep Discounts & Fast Delivery
            </Badge>
          </div>

          <h1 ref={titleRef} className={`max-w-4xl text-4xl md:text-6xl lg:text-7xl font-display font-extrabold text-white tracking-tight leading-tight mb-8 transition-all duration-700 delay-100 transform ${isTitleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Upgrade Your Home with Top-Notch Appliances
          </h1>

          <p className={`max-w-2xl text-lg md:text-xl text-gray-200 mb-10 transition-all duration-700 delay-200 transform ${isTitleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Discover unbeatable prices and exceptional service at Smart Buy Appliances. Find the perfect fit for your kitchen and laundry room today.
          </p>

          <div ref={btnRef} className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 transform ${isBtnVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Button asChild size="lg" className="btn-primary text-lg px-8 py-7 rounded-full shadow-strong">
              <Link to="tel:9197030041">
                <Phone className="mr-2 size-5" />
                Call Now: (919) 703-0041
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}